import useIsOnPage from 'app/hooks/useIsOnPage';
import useStorageState from 'app/hooks/useStorageState';
import { useGetUserInfoQuery } from 'app/hooks/user-info';
import { useAuth } from 'context';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DAYS_TO_HIDE = 60;
const STORAGE_KEY = 'set_user_names_dialog_closed_at';

const PAGES_REQUIRING_NAME = [
  '/access/share-equipment',
  '/access/create-fleet',
  '/access/share-fleet',
];

export default function useShouldShowSetUserNamesDialog() {
  const navigate = useNavigate();
  const [isNavigationBackDisabled, setIsNavigationBackDisabled] =
    useState(false);
  const isOnNameRequiredPage = useIsOnPage(PAGES_REQUIRING_NAME);

  const { isAuthenticated } = useAuth();
  const { data: user } = useGetUserInfoQuery();

  const hasAllRequiredNames = !!user?.first_name && !!user?.last_name;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [lastClosedAt, setLastClosedAt] = useStorageState<number>(
    localStorage,
    STORAGE_KEY,
    {
      isValid: lastClosedAt => moment(lastClosedAt).isValid(),
    },
  );

  const close = useCallback(() => {
    if (!isNavigationBackDisabled) {
      navigate(-1);
    }

    // NOTE: We add a brief delay before closing this dialog to ensure that
    // it won't be open one more time.
    setTimeout(() => {
      setIsDialogOpen(false);
      setLastClosedAt(new Date().getTime());
    }, 200);
  }, [isNavigationBackDisabled, setLastClosedAt]);

  const closeWithoutRedirection = useCallback(() => {
    setIsDialogOpen(false);
    setLastClosedAt(undefined);
  }, [setLastClosedAt]);

  const open = useCallback(() => {
    setIsDialogOpen(true);
    setIsNavigationBackDisabled(true);
  }, [setLastClosedAt]);

  /**
   * This dialog isn't open until the get user info request is fired.
   * To remove such behavior it's better to split our routes into Protected and Public,
   * and place UserInfoProvider into Protected one.
   */
  useEffect(() => {
    const isUserInfoReceivedSuccessfully = !!user && isAuthenticated;

    if (!isUserInfoReceivedSuccessfully || hasAllRequiredNames) {
      return;
    }

    const currentMoment = moment();
    const lastClosedMoment = moment(lastClosedAt);

    const isRequiredDaysPassed =
      currentMoment.diff(lastClosedMoment, 'days') >= DAYS_TO_HIDE;

    if (!lastClosedAt || isOnNameRequiredPage || isRequiredDaysPassed) {
      setIsDialogOpen(true);
      setLastClosedAt(undefined);
      setIsNavigationBackDisabled(false);
    }
  }, [
    isOnNameRequiredPage,
    user,
    isAuthenticated,
    lastClosedAt,
    DAYS_TO_HIDE,
    setLastClosedAt,
  ]);

  return {
    isOpen: isDialogOpen,
    hasAllRequiredNames,
    close,
    closeWithoutRedirection,
    open,
  };
}
